import { computed, ref, type ComputedRef, type Ref } from 'vue'
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'
import type { SceneManager } from '@/three/SceneManager'
import type { PathDef, Point3D } from '@/types/scene'
import type { useSceneStore, useSelectionStore } from '@/stores/scene'
import type { HistoryManager } from '@/three/HistoryManager'
import { getClickWorldPosition, type ClickGroundOptions } from '@/three/DragHandler'

type SceneStore = ReturnType<typeof useSceneStore>
type SelectionStore = ReturnType<typeof useSelectionStore>

interface PathToolDeps {
  getSm: () => SceneManager | undefined
  sceneStore: SceneStore
  selectionStore: SelectionStore
  histManager: HistoryManager
  interactionReadonly: ComputedRef<boolean>
  currentFloorId: Ref<string>
}

interface TaggedMesh extends THREE.Mesh {
  userData: {
    pathId?: string
    waypointIdx?: number
    midpointIdx?: number
    pathBody?: boolean
    [k: string]: any
  }
}

function snapOpts(deps: PathToolDeps): ClickGroundOptions {
  const g = deps.sceneStore.activeScene()?.scene?.gridSize ?? 1
  const floor = deps.sceneStore.activeFloors().find(f => f.id === deps.currentFloorId.value)
  const groundY = floor?.elevation ?? 0
  return { gridSize: g, groundY }
}

function orthogonalFromLast(last: Point3D, raw: Point3D): Point3D {
  const dx = raw.x - last.x
  const dz = raw.z - last.z
  if (Math.abs(dx) >= Math.abs(dz)) {
    return { x: raw.x, y: raw.y, z: last.z }
  }
  return { x: last.x, y: raw.y, z: raw.z }
}

export function createPathTool(deps: PathToolDeps) {
  const draftWaypoints = ref<Point3D[]>([])
  const draftPreviewCursor = ref<Point3D | null>(null)
  const draftCloseHighlight = ref(false)
  const selectedWaypointIdx = ref<number | null>(null)
  const pathRoot = new THREE.Group()
  pathRoot.name = '__paths__'

  let rafPending = false
  function scheduleSync() {
    if (rafPending) return
    rafPending = true
    requestAnimationFrame(() => {
      rafPending = false
      syncPathsVisuals()
    })
  }

  const dragState = {
    mode: '' as '' | 'waypoint' | 'path',
    pathId: '',
    waypointIdx: -1,
    start: new THREE.Vector3(),
    originalWaypoints: [] as Point3D[],
  }

  function ensureRoot() {
    const sm = deps.getSm()
    if (!sm) return
    if (!sm.scene.getObjectByName(pathRoot.name)) {
      sm.scene.add(pathRoot)
    }
  }

  function clearRoot() {
    while (pathRoot.children.length) {
      const c = pathRoot.children.pop()
      if (!c) break
      if (c instanceof THREE.Mesh) {
        c.geometry.dispose()
        const m = c.material
        if (Array.isArray(m)) m.forEach(mm => mm.dispose())
        else m.dispose()
      } else if (c instanceof THREE.Line) {
        c.geometry.dispose()
        const m = c.material
        if (Array.isArray(m)) m.forEach(mm => mm.dispose())
        else m.dispose()
      }
    }
  }

  function getPathPoints(path: PathDef) {
    return path.waypoints.map(p => new THREE.Vector3(p.x, p.y, p.z))
  }

  function makeCurvePoints(path: PathDef) {
    const points = getPathPoints(path)
    if (points.length < 2) return points
    if (!path.smooth) return points
    const curve = new THREE.CatmullRomCurve3(points, path.closed, 'catmullrom', 0.1)
    return curve.getPoints(Math.max(32, points.length * 18))
  }

  function drawPath(path: PathDef, selectedPathId: string | null) {
    if (!path.visible) return
    const isSelected = selectedPathId === path.id
    const linePoints = makeCurvePoints(path)
    if (linePoints.length < 2) return

    const lineGeo = new THREE.BufferGeometry().setFromPoints(linePoints)
    const lineMat = new THREE.LineBasicMaterial({
      color: isSelected ? '#f59e0b' : path.color,
      linewidth: 2,
    })
    const line = new THREE.Line(lineGeo, lineMat)
    line.userData.pathId = path.id
    line.userData.pathBody = true
    pathRoot.add(line)

    for (let i = 0; i < path.waypoints.length; i++) {
      const wp = path.waypoints[i]!
      const point = new THREE.Mesh(
        new THREE.SphereGeometry(isSelected ? 0.28 : 0.2, 12, 10),
        new THREE.MeshBasicMaterial({ color: isSelected ? '#fbbf24' : '#93c5fd' })
      ) as TaggedMesh
      point.position.set(wp.x, wp.y, wp.z)
      point.userData.pathId = path.id
      point.userData.waypointIdx = i
      pathRoot.add(point)

      if (i < path.waypoints.length - 1) {
        const next = path.waypoints[i + 1]!
        const mid = new THREE.Mesh(
          new THREE.SphereGeometry(0.13, 10, 8),
          new THREE.MeshBasicMaterial({ color: '#34d399', opacity: 0.8, transparent: true })
        ) as TaggedMesh
        mid.position.set((wp.x + next.x) / 2, (wp.y + next.y) / 2, (wp.z + next.z) / 2)
        mid.userData.pathId = path.id
        mid.userData.midpointIdx = i
        pathRoot.add(mid)
      }
    }
  }

  function drawDraftPath() {
    if (draftWaypoints.value.length < 1) return
    for (const wp of draftWaypoints.value) {
      const p = new THREE.Mesh(
        new THREE.SphereGeometry(0.18, 10, 8),
        new THREE.MeshBasicMaterial({ color: '#fde047' })
      )
      p.position.set(wp.x, wp.y, wp.z)
      pathRoot.add(p)
    }
    if (draftWaypoints.value.length >= 2) {
      const pts = draftWaypoints.value.map(p => new THREE.Vector3(p.x, p.y, p.z))
      const lineGeo = new THREE.BufferGeometry().setFromPoints(pts)
      const lineMat = new THREE.LineDashedMaterial({ color: '#fde047', dashSize: 0.7, gapSize: 0.35 })
      const line = new THREE.Line(lineGeo, lineMat)
      line.computeLineDistances()
      pathRoot.add(line)
    }
    const last = draftWaypoints.value[draftWaypoints.value.length - 1]!
    const preview = draftPreviewCursor.value
    if (preview) {
      const seg = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(last.x, last.y, last.z),
        new THREE.Vector3(preview.x, preview.y, preview.z),
      ])
      const rubber = new THREE.Line(
        seg,
        new THREE.LineDashedMaterial({ color: 0xfacc15, dashSize: 0.5, gapSize: 0.25 })
      )
      rubber.computeLineDistances()
      pathRoot.add(rubber)
    }
    if (draftCloseHighlight.value && draftWaypoints.value.length >= 2) {
      const first = draftWaypoints.value[0]!
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.35, 0.55, 24),
        new THREE.MeshBasicMaterial({ color: 0xfbbf24, side: THREE.DoubleSide })
      )
      ring.rotation.x = -Math.PI / 2
      ring.position.set(first.x, first.y + 0.02, first.z)
      pathRoot.add(ring)
    }
  }

  function syncPathsVisuals() {
    ensureRoot()
    clearRoot()
    const selectedObjectId = deps.selectionStore.selectedId
    const selectedObjPathId = deps.sceneStore.activeObjects().find(o => o.id === selectedObjectId)?.pathId ?? null
    const selectedPathId = deps.selectionStore.selectedPathId ?? selectedObjPathId
    for (const path of deps.sceneStore.activePaths()) drawPath(path, selectedPathId)
    drawDraftPath()
    deps.getSm()?.markDirty()
  }

  function getGroundY() {
    const floor = deps.sceneStore.activeFloors().find(f => f.id === deps.currentFloorId.value)
    return floor?.elevation ?? 0
  }

  function distXZ(a: Point3D, b: Point3D) {
    return Math.hypot(a.x - b.x, a.z - b.z)
  }

  function appendDraftWaypointByClick(ev: MouseEvent) {
    const sm = deps.getSm()
    if (!sm || deps.interactionReadonly.value) return false
    const canvas = sm.renderer.domElement
    const pos = getClickWorldPosition(ev, canvas, sm.camera, snapOpts(deps))
    if (!pos) return false
    const grid = deps.sceneStore.activeScene()?.scene?.gridSize ?? 1
    let p: Point3D = { x: pos.x, y: pos.y, z: pos.z }

    if (draftWaypoints.value.length >= 2) {
      const first = draftWaypoints.value[0]!
      if (distXZ(p, first) < 0.35 * grid) {
        finishDraftPath(true)
        return true
      }
    }

    if (ev.shiftKey && draftWaypoints.value.length >= 1) {
      const last = draftWaypoints.value[draftWaypoints.value.length - 1]!
      p = orthogonalFromLast(last, p)
    }

    draftWaypoints.value.push(p)
    draftPreviewCursor.value = null
    syncPathsVisuals()
    return true
  }

  function onPathDraftPointerMove(ev: MouseEvent) {
    if (draftWaypoints.value.length < 1) {
      draftPreviewCursor.value = null
      draftCloseHighlight.value = false
      return
    }
    const sm = deps.getSm()
    if (!sm) return
    const pos = getClickWorldPosition(ev, sm.renderer.domElement, sm.camera, snapOpts(deps))
    if (!pos) return
    let p: Point3D = { x: pos.x, y: pos.y, z: pos.z }
    if (ev.shiftKey) {
      const last = draftWaypoints.value[draftWaypoints.value.length - 1]!
      p = orthogonalFromLast(last, p)
    }
    draftPreviewCursor.value = p
    const grid = deps.sceneStore.activeScene()?.scene?.gridSize ?? 1
    const first = draftWaypoints.value[0]!
    draftCloseHighlight.value = draftWaypoints.value.length >= 2 && distXZ(p, first) < 0.35 * grid
    scheduleSync()
  }

  function finishDraftPath(closed = false) {
    if (draftWaypoints.value.length < 2) return
    const newPath: PathDef = {
      id: uuidv4(),
      name: `路径-${deps.sceneStore.activePaths().length + 1}`,
      type: 'path',
      waypoints: [...draftWaypoints.value],
      closed,
      smooth: false,
      visible: true,
      color: '#3b82f6',
      speedLimit: null,
    }
    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    deps.sceneStore.addPath(newPath)
    deps.selectionStore.selectPath(newPath.id)
    // 完成一条路径（Enter/双击/点起点闭合）后必须退出路径工具，不能停留在"待绘制"状态
    // 直接开始下一条——要画下一条必须重新点路径工具按钮进入。
    deps.selectionStore.setPathToolActive(false)
    draftWaypoints.value = []
    draftPreviewCursor.value = null
    draftCloseHighlight.value = false
    syncPathsVisuals()
  }

  function clearDraftPath() {
    draftWaypoints.value = []
    draftPreviewCursor.value = null
    draftCloseHighlight.value = false
    syncPathsVisuals()
  }

  function pickPathHandle(ev: MouseEvent) {
    const sm = deps.getSm()
    if (!sm) return null
    const rect = sm.renderer.domElement.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((ev.clientX - rect.left) / rect.width) * 2 - 1,
      -((ev.clientY - rect.top) / rect.height) * 2 + 1
    )
    const ray = new THREE.Raycaster()
    ray.setFromCamera(ndc, sm.camera)
    const hits = ray.intersectObjects(pathRoot.children, true)
    return hits[0]?.object as TaggedMesh | undefined
  }

  function handlePathClick(ev: MouseEvent) {
    if (deps.interactionReadonly.value) return false
    const hit = pickPathHandle(ev)
    if (!hit?.userData?.pathId) return false
    const pathId = hit.userData.pathId
    deps.selectionStore.selectPath(pathId)
    if (typeof hit.userData.midpointIdx === 'number') {
      const path = deps.sceneStore.activePaths().find(p => p.id === pathId)
      if (!path) return true
      const i = hit.userData.midpointIdx
      const a = path.waypoints[i]!
      const b = path.waypoints[i + 1]!
      const inserted = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: (a.z + b.z) / 2 }
      const next = [...path.waypoints]
      next.splice(i + 1, 0, inserted)
      deps.histManager.push(deps.sceneStore.getEditorSnapshot())
      deps.sceneStore.updatePath(pathId, { waypoints: next })
      selectedWaypointIdx.value = i + 1
      syncPathsVisuals()
    } else if (typeof hit.userData.waypointIdx === 'number') {
      selectedWaypointIdx.value = hit.userData.waypointIdx
      syncPathsVisuals()
    } else {
      selectedWaypointIdx.value = null
      syncPathsVisuals()
    }
    return true
  }

  function deleteSelectedWaypoint() {
    const pathId = deps.selectionStore.selectedPathId
    const idx = selectedWaypointIdx.value
    if (!pathId || idx == null) return false
    const path = deps.sceneStore.activePaths().find(p => p.id === pathId)
    if (!path || path.waypoints.length <= 2) return false
    const next = [...path.waypoints]
    next.splice(idx, 1)
    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    deps.sceneStore.updatePath(pathId, { waypoints: next })
    selectedWaypointIdx.value = Math.min(idx, next.length - 1)
    syncPathsVisuals()
    return true
  }

  function startDrag(ev: PointerEvent) {
    if (deps.interactionReadonly.value) return false
    const hit = pickPathHandle(ev)
    if (!hit?.userData?.pathId) return false
    const sm = deps.getSm()
    if (!sm) return false
    const pos = getClickWorldPosition(ev, sm.renderer.domElement, sm.camera, snapOpts(deps))
    if (!pos) return false
    dragState.pathId = hit.userData.pathId
    dragState.start.set(pos.x, getGroundY(), pos.z)
    const path = deps.sceneStore.activePaths().find(p => p.id === dragState.pathId)
    if (!path) return false
    dragState.originalWaypoints = path.waypoints.map((p: Point3D) => ({ ...p }))
    if (typeof hit.userData.waypointIdx === 'number') {
      dragState.mode = 'waypoint'
      dragState.waypointIdx = hit.userData.waypointIdx
    } else if (hit.userData.pathBody) {
      dragState.mode = 'path'
      dragState.waypointIdx = -1
    } else {
      return false
    }
    deps.selectionStore.selectPath(dragState.pathId)
    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    return true
  }

  function drag(ev: PointerEvent) {
    if (!dragState.mode) return
    const sm = deps.getSm()
    if (!sm) return
    const pos = getClickWorldPosition(ev, sm.renderer.domElement, sm.camera, snapOpts(deps))
    if (!pos) return
    const current = new THREE.Vector3(pos.x, getGroundY(), pos.z)
    const dx = current.x - dragState.start.x
    const dz = current.z - dragState.start.z
    const next = dragState.originalWaypoints.map(p => ({ ...p }))
    if (dragState.mode === 'waypoint' && dragState.waypointIdx >= 0) {
      const i = dragState.waypointIdx
      next[i] = { ...next[i], x: next[i]!.x + dx, z: next[i]!.z + dz }
    } else if (dragState.mode === 'path') {
      for (const p of next) {
        p.x += dx
        p.z += dz
      }
    }
    deps.sceneStore.updatePath(dragState.pathId, { waypoints: next })
    syncPathsVisuals()
  }

  function stopDrag() {
    dragState.mode = ''
    dragState.pathId = ''
    dragState.waypointIdx = -1
    dragState.originalWaypoints = []
  }

  function disposePathTool() {
    clearRoot()
    pathRoot.parent?.remove(pathRoot)
  }

  const hasDraft = computed(() => draftWaypoints.value.length > 0)
  return {
    syncPathsVisuals,
    appendDraftWaypointByClick,
    finishDraftPath,
    clearDraftPath,
    onPathDraftPointerMove,
    handlePathClick,
    deleteSelectedWaypoint,
    startDrag,
    drag,
    stopDrag,
    disposePathTool,
    hasDraft,
  }
}
